import { create } from 'zustand';
import { Group } from 'three';
import { AudioListener } from 'three/webgpu';
import * as THREE from 'three/webgpu';
import type { Terrain } from '../../yosemite/terrain';
import type { Forest } from '../../yosemite/trees';

export enum CameraMode {
  Follow  = 0,
  FPV = 1,
  Detached = 2,
  Drone = 3,
}

interface GameState {
  // ===== Camera State =====
  cameraMode: CameraMode;
  setCameraMode: (mode: CameraMode) => void;
  toggleCameraMode: () => void;
  
  // ===== Character State =====
  characterRef: React.MutableRefObject<Group | null> | null;
  setCharacterRef: (ref: React.MutableRefObject<Group | null> | null) => void;

  activeTargets: string[];
  setActiveTargets: (targets: string[]) => void;

  readyStatus: Record<string, boolean>;
  setComponentReady: (id: string, isReady: boolean) => void;

  isSceneReady: () => boolean;

  isGameStarted: boolean;
  setIsGameStarted: (loaded: boolean) => void;

  isSoundOn: boolean;
  setIsSoundOn: (isSoundOn: boolean) => void;

  audioListener: AudioListener | null;
  setAudioListener: (listener: THREE.AudioListener) => void;

  isMobile: boolean;
  setIsMobile: (isMobile: boolean) => void;

  quality: 'low' | 'high';
  toggleQuality: () => void;
  setQuality: (quality: 'low' | 'high') => void;

  isControlEnabled: boolean; 
  setControlEnabled: (enabled: boolean) => void;

  // ===== WebGPU State =====
  gpuError: string | null;
  setGpuError: (error: string | null) => void;

  // ===== Rex Attack Alert =====
  rexAlertAt: number;
  triggerRexAlert: () => void;

  // ===== Player Health / Survival =====
  hp: number;
  maxHp: number;
  lastHitAt: number;
  isPlayerDead: boolean;
  knockback: THREE.Vector3;
  takeDamage: (amount: number) => void;
  respawn: () => void;

  // ===== Weapon / Ammo =====
  ammo: number;
  maxAmmo: number;
  useAmmo: () => void;
  addAmmo: (amount: number) => void;

  // ===== Yosemite World =====
  terrain: Terrain | null;
  setTerrain: (terrain: Terrain | null) => void;
  forest: Forest | null;
  setForest: (forest: Forest | null) => void;

  // ===== Hex Pavilion Interaction =====
  hexPavilionNear: boolean;
  setHexPavilionNear: (near: boolean) => void;

  // ===== Imported Scene (.glb/.gltf from SketchUp exporter) =====
  importedModel: { url: string; name: string } | null;
  setImportedModel: (model: { url: string; name: string } | null) => void;
  importError: string | null;
  setImportError: (error: string | null) => void;
  // Turntable-style transform controls (updated by the UI toolbar)
  importRotY: number;
  importScaleMul: number;
  setImportRot: (deltaRad: number) => void;
  setImportScale: (multiplier: number) => void;
  // Bump these to ask the canvas to re-frame / re-place the model
  importViewTick: number;
  requestImportView: () => void;
}

export const useGameStore = create<GameState>((set, get) => ({
  // ===== Camera State =====
  cameraMode: CameraMode.Follow,
  setCameraMode: (mode) => set({ cameraMode: mode }),
  toggleCameraMode: () => set((state) => {
    // Drone is a desktop keyboard+pointer experience; mobile never cycles
    // into it (touches keep using Follow / FPV / Detached).
    const mobileOnly = state.isMobile;
    const step = (m: CameraMode) => (m + 1) % 4;
    let next = step(state.cameraMode);
    if (mobileOnly && next === CameraMode.Drone) next = step(next);
    return { cameraMode: next };
  }),
  
  // ===== Character State =====
  characterRef: null,
  setCharacterRef: (ref) => set({ characterRef: ref }),

  activeTargets: [],
  setActiveTargets: (targets) => set({ activeTargets: targets }),

  readyStatus: {},
  setComponentReady: (id, isReady) => set((state) => ({
    readyStatus: { ...state.readyStatus, [id]: isReady }
  })),

  isSceneReady: () => {
    const { activeTargets, readyStatus } = get();
    if (activeTargets.length === 0) return false;
    return activeTargets.every((target) => readyStatus[target] === true);
  },

  isGameStarted: false,
  setIsGameStarted: (loaded) => set({ isGameStarted: loaded }),

  isSoundOn: false,
  setIsSoundOn: (isSoundOn) => set({ isSoundOn: isSoundOn }),

  audioListener: null,
  setAudioListener: (listener) => set({ audioListener: listener }),

  isMobile: false,
  setIsMobile: (isMobile) => set({ isMobile: isMobile }),

  quality: 'high',
  toggleQuality: () => set((state) => ({ quality: state.quality === 'high' ? 'low' : 'high' })),
  setQuality: (quality) => set({ quality }),

  isControlEnabled: false,
  setControlEnabled: (enabled) => set({ isControlEnabled: enabled }),

  // ===== WebGPU State =====
  gpuError: null,
  setGpuError: (error) => set({ gpuError: error }),

  // ===== Rex Attack Alert =====
  rexAlertAt: 0,
  triggerRexAlert: () => set({ rexAlertAt: Date.now() }),

  // ===== Player Health / Survival =====
  hp: 100,
  maxHp: 100,
  lastHitAt: 0,
  isPlayerDead: false,
  knockback: new THREE.Vector3(0, 0, 0),
  takeDamage: (amount) => {
    const s = get();
    if (s.isPlayerDead) return;
    const newHp = Math.max(0, s.hp - amount);
    set({ hp: newHp, lastHitAt: Date.now() });
    if (newHp <= 0) set({ isPlayerDead: true });
  },
  respawn: () => {
    const ref = get().characterRef;
    if (ref?.current) ref.current.position.set(0, 0, 0);
    get().knockback.set(0, 0, 0);
    set({ hp: 100, isPlayerDead: false, lastHitAt: 0 });
  },

  // ===== Weapon / Ammo =====
  ammo: 30,
  maxAmmo: 150,
  useAmmo: () => set((s) => ({ ammo: Math.max(0, s.ammo - 1) })),
  addAmmo: (amount) => set((s) => ({ ammo: Math.min(s.maxAmmo, s.ammo + amount) })),

  // ===== Yosemite World =====
  terrain: null,
  setTerrain: (terrain) => set({ terrain }),
  forest: null,
  setForest: (forest) => set({ forest }),

  // ===== Hex Pavilion Interaction =====
  hexPavilionNear: false,
  setHexPavilionNear: (near) => set({ hexPavilionNear: near }),

  // ===== Imported Scene =====
  importedModel: null,
  setImportedModel: (model) => set({ importedModel: model }),
  importError: null,
  setImportError: (error) => set({ importError: error }),
  importRotY: 0,
  importScaleMul: 1,
  setImportRot: (deltaRad) => set((state) => ({ importRotY: state.importRotY + deltaRad })),
  setImportScale: (multiplier) => set((state) => ({
    importScaleMul: Math.min(6, Math.max(0.15, state.importScaleMul * multiplier)),
  })),
  importViewTick: 0,
  requestImportView: () => set((state) => ({ importViewTick: state.importViewTick + 1 })),
}));
